import type {ErrorEvent} from "@sentry/react";

import {SYNTHETIC_ERROR_CODES} from "./error-codes";
import {getEventTitle, getTelemetryTitle} from "./error-titles";
import {shouldDropEventByTags} from "./should-capture";

/** GlitchTip accepts `title`; Sentry's ErrorEvent type omits it. */
type GroupableErrorEvent = ErrorEvent & {title?: string};

const SENSITIVE_HEADER_KEYS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
]);

const SENSITIVE_EXTRA_KEYS = new Set([
  "password",
  "token",
  "access_token",
  "refresh_token",
  "authorization",
  "email",
  "first_name",
  "last_name",
  "user_name",
  "telephone",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "coordinates",
  "gps",
  "plate",
  "vin",
  "license_plate",
  "address",
  "body",
  "notes",
]);

const DROPPED_HTTP_STATUSES = new Set([401, 403, 404, 422]);

function stripQueryFromUrl(url: string): string {
  try {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const parsed = new URL(url);
      parsed.search = "";
      parsed.hash = "";
      return parsed.toString();
    }
  } catch {
    // fall through
  }
  return url.split("?")[0]?.split("#")[0] ?? url;
}

function scrubObjectKeys(
  value: Record<string, unknown> | undefined,
  sensitiveKeys: Set<string>,
): void {
  if (!value) return;

  for (const key of Object.keys(value)) {
    if (sensitiveKeys.has(key.toLowerCase())) {
      delete value[key];
    }
  }
}

function getHttpStatusFromEvent(event: ErrorEvent): number | undefined {
  const tagStatus = event.tags?.http_status;
  if (tagStatus !== undefined && tagStatus !== "0" && tagStatus !== "network") {
    const parsed = Number(tagStatus);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const exceptionValue = event.exception?.values?.[0]?.value ?? event.message ?? "";
  const match = /status code (\d+)/i.exec(exceptionValue);
  if (match) return Number(match[1]);

  return undefined;
}

function shouldDropRoutineHttpEvent(event: ErrorEvent): boolean {
  if (event.tags?.error_source === "telemetry") return false;

  const source = event.tags?.error_source;
  if (source !== "axios" && source !== "boundary") return false;

  const status = getHttpStatusFromEvent(event);
  if (status === undefined) return false;
  return DROPPED_HTTP_STATUSES.has(status);
}

function scrubSentryEvent(event: ErrorEvent): ErrorEvent | null {
  if (event.user) {
    const userId = event.user.id;
    event.user =
      userId !== undefined && userId !== null ? {id: String(userId)} : {};
  }

  if (event.request) {
    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (SENSITIVE_HEADER_KEYS.has(key.toLowerCase())) {
          delete event.request.headers[key];
        }
      }
    }

    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;

    if (typeof event.request.url === "string") {
      event.request.url = stripQueryFromUrl(event.request.url);
    }
  }

  scrubObjectKeys(event.extra, SENSITIVE_EXTRA_KEYS);
  scrubObjectKeys(event.contexts?.extra as Record<string, unknown> | undefined, SENSITIVE_EXTRA_KEYS);

  const diagnosticsContext = event.contexts?.diagnostics;
  if (diagnosticsContext && typeof diagnosticsContext === "object" && !Array.isArray(diagnosticsContext)) {
    scrubObjectKeys(diagnosticsContext as Record<string, unknown>, SENSITIVE_EXTRA_KEYS);
  }

  if (event.breadcrumbs) {
    for (const breadcrumb of event.breadcrumbs) {
      if (typeof breadcrumb.message === "string") {
        breadcrumb.message = breadcrumb.message.replace(
          /(Bearer\s+)[^\s]+/gi,
          "$1[Filtered]",
        );
      }

      if (breadcrumb.data) {
        scrubObjectKeys(breadcrumb.data as Record<string, unknown>, SENSITIVE_EXTRA_KEYS);
        if (typeof breadcrumb.data.url === "string") {
          breadcrumb.data.url = stripQueryFromUrl(breadcrumb.data.url);
        }
      }
    }
  }

  return event;
}

export function applyEventGrouping(event: GroupableErrorEvent): void {
  if (event.tags?.error_source === "telemetry") {
    const feature = event.tags?.feature;
    const eventType = event.tags?.event_type;
    if (typeof feature === "string" && typeof eventType === "string") {
      const title = getTelemetryTitle(feature, eventType);
      event.title = title;
      event.message = title;
    }
    return;
  }

  const feature = event.tags?.feature;
  const errorCode =
    (typeof event.tags?.error_code === "string" && event.tags.error_code) ||
    SYNTHETIC_ERROR_CODES.UNHANDLED_REJECTION;

  if (typeof feature === "string") {
    const title = getEventTitle(feature, errorCode);
    event.title = title;
    event.message = title;

    const failureDomain = event.tags?.failure_domain;
    const realtimePhase = event.tags?.realtime_failure_phase;
    if (failureDomain === "realtime") {
      event.fingerprint = [
        "realtime",
        errorCode,
        typeof realtimePhase === "string" ? realtimePhase : "unknown",
      ];
    } else {
      event.fingerprint = [feature, errorCode];
    }

    if (event.exception?.values?.length) {
      const preservedKind =
        typeof event.extra?.error_kind === "string" ? event.extra.error_kind : undefined;
      for (const value of event.exception.values) {
        value.value = title;
      }
      if (preservedKind && event.extra) {
        event.extra.error_kind = preservedKind;
      }
    }
  }

  const userRole = event.tags?.user_role;
  if (userRole !== undefined) {
    if (typeof userRole !== "string" || userRole.startsWith("{") || userRole.startsWith("[")) {
      delete event.tags!.user_role;
    }
  }
}

export function beforeSendEvent(event: ErrorEvent): ErrorEvent | null {
  if (shouldDropEventByTags(event.tags)) return null;
  if (shouldDropRoutineHttpEvent(event)) return null;

  const scrubbed = scrubSentryEvent(event);
  if (!scrubbed) return null;
  applyEventGrouping(scrubbed);
  return scrubbed;
}
